// ─────────────────────────────────────────────────────────────────────────────
// NcTreePanel.jsx — Form düzenleyici (Forms/FormEditorPage) yan paneli
// Seçili soruya ait Uygunsuzluk → Tehlike → Risk ağacını yönetir.
// inspectionAdminService üzerinden doğrudan API çağrısı yapar.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useEffect, useState, useCallback } from "react";
import { Badge, Spinner, Alert } from "reactstrap";
import { Plus, Edit2, Trash2, ChevronRight, ChevronLeft, X, AlertTriangle, Lightbulb, ChevronDown } from "lucide-react";
import * as adminSvc from "../../services/inspectionAdminService";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";
import "./ncTreePanel.css";

// ── Level konfigürasyonu (sadece 3 seviye: Haz → Risk → NC) ──────────────────
const PANEL_LEVELS = [
  {
    key: "hazards",         get label() { return i18n.t("formlar:ncTreePanel.uygunsuzluk"); },  get plural() { return i18n.t("formlar:ncTreePanel.uygunsuzluklar"); },
    idField: "inspection_hazard_id",          nameField: "inspection_hazard_name",
    color: "warning",
    fields: [
      { key: "name",        dataKey: "inspection_hazard_name",        get label() { return i18n.t("formlar:ncTreePanel.uygunsuzlukAdi"); }, type: "textarea", required: true },
      { key: "description", dataKey: "inspection_hazard_description", get label() { return i18n.t("formlar:ncTreePanel.aciklama"); },         type: "text" },
    ],
  },
  {
    key: "risks",           get label() { return i18n.t("formlar:ncTreePanel.tehlike"); },      get plural() { return i18n.t("formlar:ncTreePanel.tehlikeler"); },
    idField: "inspection_risk_id",            nameField: "inspection_risk_name",
    color: "danger",
    fields: [
      { key: "name",        dataKey: "inspection_risk_name",        get label() { return i18n.t("formlar:ncTreePanel.tehlikeAdi"); }, type: "textarea", required: true },
      { key: "description", dataKey: "inspection_risk_description", get label() { return i18n.t("formlar:ncTreePanel.aciklama"); },    type: "text" },
    ],
  },
  {
    key: "nonconformities", get label() { return i18n.t("formlar:ncTreePanel.risk"); },          get plural() { return i18n.t("formlar:ncTreePanel.riskler"); },
    idField: "inspection_nonconformity_id",   nameField: "inspection_nonconformity_name",
    color: "danger", isLeaf: true,
    fields: [
      { key: "name",        dataKey: "inspection_nonconformity_name",        get label() { return i18n.t("formlar:ncTreePanel.riskAdi"); },  type: "textarea",     required: true },
      { key: "description", dataKey: "inspection_nonconformity_description", get label() { return i18n.t("formlar:ncTreePanel.aciklama"); },  type: "text" },
      { key: "olasilik", get label() { return i18n.t("formlar:ncTreePanel.olasilik"); }, type: "select", options: [
          { value: "",   get label() { return i18n.t("formlar:ncTreePanel.seciniz"); } },
          { value: 10,   get label() { return i18n.t("formlar:ncTreePanel.n10BeklenirKesin"); } },
          { value: 6,    get label() { return i18n.t("formlar:ncTreePanel.n6YuksekOldukcaMumkun"); } },
          { value: 3,    get label() { return i18n.t("formlar:ncTreePanel.n3Olasi"); } },
          { value: 1,    get label() { return i18n.t("formlar:ncTreePanel.n1NadirenAmaMumkun"); } },
          { value: 0.5,  get label() { return i18n.t("formlar:ncTreePanel.n05BeklenemezAmaMumkun"); } },
          { value: 0.2,  get label() { return i18n.t("formlar:ncTreePanel.n02PratikteMumkunDegil"); } },
          { value: 0.1,  get label() { return i18n.t("formlar:ncTreePanel.n01SadeceTeorikOlarak"); } },
        ]},
      { key: "frekans", get label() { return i18n.t("formlar:ncTreePanel.frekansMaruziyet"); }, type: "select", options: [
          { value: "",   get label() { return i18n.t("formlar:ncTreePanel.seciniz"); } },
          { value: 10,   get label() { return i18n.t("formlar:ncTreePanel.n10HemenHemenSurekliSaatte"); } },
          { value: 6,    get label() { return i18n.t("formlar:ncTreePanel.n6SikGunde1Veya"); } },
          { value: 3,    get label() { return i18n.t("formlar:ncTreePanel.n3AraSiraHaftadaBir"); } },
          { value: 2,    get label() { return i18n.t("formlar:ncTreePanel.n2SikDegilAydaBir"); } },
          { value: 1,    get label() { return i18n.t("formlar:ncTreePanel.n1SeyrekYildaBirkacDefa"); } },
          { value: 0.5,  get label() { return i18n.t("formlar:ncTreePanel.n05CokSeyrekYilda"); } },
        ]},
      { key: "siddet", get label() { return i18n.t("formlar:ncTreePanel.siddetSonuc"); }, type: "select", options: [
          { value: "",   get label() { return i18n.t("formlar:ncTreePanel.seciniz"); } },
          { value: 100,  get label() { return i18n.t("formlar:ncTreePanel.n100BirdenFazlaOlumluKaza"); } },
          { value: 40,   get label() { return i18n.t("formlar:ncTreePanel.n40OlumculKazaCiddiCevresel"); } },
          { value: 15,   get label() { return i18n.t("formlar:ncTreePanel.n15KaliciHasarYaralanmaIs"); } },
          { value: 7,    get label() { return i18n.t("formlar:ncTreePanel.n7CevreselEngelOnemliHasar"); } },
          { value: 3,    get label() { return i18n.t("formlar:ncTreePanel.n3KucukHasarYaralanmaDahili"); } },
          { value: 1,    get label() { return i18n.t("formlar:ncTreePanel.n1UcuzAtlatmaCevreselZarar"); } },
        ]},
      { key: "riskDegeri",  dataKey: "risk_degeri",     get label() { return i18n.t("formlar:ncTreePanel.riskDegeri"); },       type: "number", calculated: true },
      { key: "oneri",       get label() { return i18n.t("formlar:ncTreePanel.oneri"); },             type: "textarea" },
    ],
  },
];

function getId(cfg, item)   { return item[cfg.idField]; }
function getName(cfg, item) { return item?.translated_name || item?.[cfg.nameField] || "—"; }

// ── Risk değerine göre badge sınıfı ──────────────────────────────────────────
function riskBadgeClass(val) {
  if (!val) return "";
  if (val < 20)  return "nc-suggester-item-badge--low";
  if (val < 70)  return "nc-suggester-item-badge--mid";
  if (val < 200) return "nc-suggester-item-badge--high";
  return "nc-suggester-item-badge--crit";
}

// ── Risk Suggester — aynı hazard altındaki önceki riskleri listeler ──────────
function RiskSuggester({ hazardId, onSelect }) {
  const { t } = useTranslation("formlar");
  const [items,    setItems]    = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [open,     setOpen]     = useState(true);

  useEffect(() => {
    if (!hazardId) return;
    let cancelled = false;
    async function fetch() {
      setLoading(true);
      try {
        const data = await adminSvc.getHazardNonconformities(hazardId, { languageId: 1 });
        if (!cancelled) setItems(data);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetch();
    return () => { cancelled = true; };
  }, [hazardId]);

  if (!hazardId) return null;

  return (
    <div className="nc-suggester">
      <div className="nc-suggester-header" onClick={() => setOpen((p) => !p)}>
        <span className="nc-suggester-title">
          <Lightbulb size={11} />{" "}{t("ncTreePanel.oncekiRisklerdenDoldur")}
        </span>
        <ChevronDown size={12} style={{ color: "#92400e", transform: open ? "rotate(180deg)" : "none", transition: "transform .2s" }} />
      </div>

      {open && (
        <div className="nc-suggester-body">
          {loading ? (
            <div className="text-center py-2"><Spinner size="sm" color="warning" /></div>
          ) : items.length === 0 ? (
            <p className="nc-suggester-empty">{t("ncTreePanel.buUygunsuzlukIcinHenuzKayitli")}</p>
          ) : (
            items.map((s) => (
              <div
                key={s.inspection_nonconformity_id}
                className="nc-suggester-item"
                title={t("ncTreePanel.tiklaFormaDoldur")}
                onClick={() => onSelect(s)}
              >
                <div className="nc-suggester-item-info">
                  <div className="nc-suggester-item-name">{s.nonconformity_name || "—"}</div>
                  {s.risk_name && (
                    <div className="nc-suggester-item-parent">↳ {s.risk_name}</div>
                  )}
                </div>
                {s.risk_degeri != null && (
                  <span className={`nc-suggester-item-badge ${riskBadgeClass(s.risk_degeri)}`}>
                    {s.risk_degeri}
                  </span>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ── Inline form (modal yerine — panel içinde daha kompakt) ────────────────────
function InlineForm({ levelCfg, parentId, item, onSave, onCancel, hazardId }) {
  const { t } = useTranslation("formlar");
  const isEdit = !!item;
  const isNc   = levelCfg.key === "nonconformities";

  const [form,   setForm]   = useState(() => {
    const init = {};
    levelCfg.fields.forEach((f) => {
      init[f.key] = item?.[f.dataKey ?? f.key] ?? (f.type === "number" ? "" : "");
    });
    return init;
  });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState(null);

  // Suggester'dan bir öneri seçildiğinde formu doldur
  function applysuggestion(s) {
    setForm((prev) => {
      const o = Number(s.olasilik ?? prev.olasilik ?? 0);
      const f = Number(s.frekans  ?? prev.frekans  ?? 0);
      const sv = Number(s.siddet  ?? prev.siddet   ?? 0);
      return {
        ...prev,
        name:       s.nonconformity_name ?? prev.name,
        olasilik:   s.olasilik  ?? prev.olasilik,
        frekans:    s.frekans   ?? prev.frekans,
        siddet:     s.siddet    ?? prev.siddet,
        riskDegeri: (o > 0 && f > 0 && sv > 0) ? +(o * f * sv).toFixed(2) : prev.riskDegeri,
        oneri:      s.oneri     ?? prev.oneri,
      };
    });
  }

  function set(key, val) {
    setForm((p) => {
      const updated = { ...p, [key]: val };
      if (["olasilik", "frekans", "siddet"].includes(key)) {
        const o = Number(key === "olasilik" ? val : updated.olasilik);
        const f = Number(key === "frekans"  ? val : updated.frekans);
        const s = Number(key === "siddet"   ? val : updated.siddet);
        updated.riskDegeri = (o > 0 && f > 0 && s > 0) ? +(o * f * s).toFixed(2) : "";
      }
      return updated;
    });
  }

  async function submit() {
    const missing = levelCfg.fields.find((f) => f.required && !form[f.key]?.toString().trim());
    if (missing) { setError(t("ncTreePanel.zorunludur", { label: missing.label })); return; }
    setSaving(true); setError(null);
    try {
      const body = { ...form };
      if (!isEdit && parentId != null) body.parentId = parentId;
      levelCfg.fields.forEach((f) => {
        if ((f.type === "number" || ["olasilik","frekans","siddet","riskDegeri"].includes(f.key)) && body[f.key] !== "") body[f.key] = Number(body[f.key]);
      });
      if (isEdit) await adminSvc.updateItem(levelCfg.key, getId(levelCfg, item), body);
      else        await adminSvc.createItem(levelCfg.key, body);
      onSave();
    } catch (e) {
      setError(e?.response?.data?.msg || t("ncTreePanel.islemBasarisizOldu"));
    } finally { setSaving(false); }
  }

  return (
    <div className="nc-inline-form">
      {/* Risk Suggester — sadece nonconformity (Risk) seviyesinde göster */}
      {isNc && hazardId && (
        <RiskSuggester hazardId={hazardId} onSelect={applysuggestion} />
      )}
      {error && <div className="text-danger small mb-2 mt-2">{error}</div>}
      {levelCfg.fields.map((f) => (
        <div key={f.key} className="nc-inline-field">
          <label className="nc-inline-label">
            {f.label}{f.required && <span className="text-danger ms-1">*</span>}
          </label>
          {f.type === "textarea" ? (
            <textarea
              className="form-control form-control-sm"
              rows={2}
              value={form[f.key] ?? ""}
              onChange={(e) => set(f.key, e.target.value)}
            />
          ) : f.type === "select" ? (
            <select
              className="form-select form-select-sm"
              value={form[f.key] ?? ""}
              onChange={(e) => set(f.key, e.target.value)}
            >
              {(f.options || []).map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          ) : (
            <>
              <input
                type={f.type === "number" ? "number" : "text"}
                className={`form-control form-control-sm${f.calculated ? " bg-light" : ""}`}
                value={form[f.key] ?? ""}
                min={f.min} max={f.max}
                readOnly={!!f.calculated}
                onChange={(e) => !f.calculated && set(f.key, e.target.value)}
              />
              {f.calculated && (
                <small className="text-muted" style={{ fontSize: "0.68rem" }}>{t("ncTreePanel.olasilikFrekansSiddet")}</small>
              )}
            </>
          )}
        </div>
      ))}
      <div className="d-flex gap-1 justify-content-end mt-2">
        <button className="btn btn-sm btn-light border" onClick={onCancel}>{t("common:iptal")}</button>
        <button className="btn btn-sm btn-primary" onClick={submit} disabled={saving}>
          {saving ? <Spinner size="sm" /> : isEdit ? t("ncTreePanel.guncelle") : t("common:ekle")}
        </button>
      </div>
    </div>
  );
}

// ── Seviye listesi ─────────────────────────────────────────────────────────────
function LevelList({ levelCfg, parentId, onDrillDown, hazardId }) {
  const { t } = useTranslation("formlar");
  const [items,    setItems]    = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [showAdd,  setShowAdd]  = useState(false);
  const [editItem, setEditItem] = useState(null);

  const load = useCallback(async () => {
    if (parentId == null) return;
    setLoading(true);
    try {
      const data = await adminSvc.listItems(levelCfg.key, { parentId, languageId: 1 });
      setItems(data);
    } catch { /* sessiz */ }
    finally { setLoading(false); }
  }, [levelCfg.key, parentId]);

  useEffect(() => { async function run() { await load(); } run(); }, [load]);

  async function handleDelete(item) {
    const name = getName(levelCfg, item);
    if (!window.confirm(t("ncTreePanel.silinsinMiSoftDelete", { name }))) return;
    try {
      await adminSvc.softDeleteItem(levelCfg.key, getId(levelCfg, item));
      load();
    } catch (e) { alert(e?.response?.data?.msg || t("ncTreePanel.silmeBasarisiz")); }
  }

  return (
    <div>
      {/* Başlık + Ekle */}
      <div className="d-flex align-items-center justify-content-between mb-2">
        <Badge color={levelCfg.color} pill className="px-2" style={{ fontSize: "0.68rem" }}>
          {levelCfg.plural}
        </Badge>
        {!showAdd && (
          <button
            className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1"
            style={{ fontSize: "0.72rem", padding: "2px 8px" }}
            onClick={() => { setShowAdd(true); setEditItem(null); }}
          >
            <Plus size={11} />{" "}{t("ncTreePanel.yeni", { label: levelCfg.label })}
          </button>
        )}
      </div>

      {/* Yeni öğe formu */}
      {showAdd && (
        <InlineForm
          levelCfg={levelCfg} parentId={parentId} item={null}
          hazardId={hazardId}
          onSave={() => { setShowAdd(false); load(); }}
          onCancel={() => setShowAdd(false)}
        />
      )}

      {/* Liste */}
      {loading ? (
        <div className="text-center py-2"><Spinner size="sm" color={levelCfg.color} /></div>
      ) : items.length === 0 ? (
        <p className="text-muted text-center mb-0" style={{ fontSize: "0.75rem", padding: "12px 0" }}>
          {t("ncTreePanel.henuzEklenmemis", { v: levelCfg.label.toLowerCase() })}
        </p>
      ) : (
        <div className="d-flex flex-column gap-1">
          {items.map((item) => {
            const id   = getId(levelCfg, item);
            const name = getName(levelCfg, item);
            const isEditing = editItem && getId(levelCfg, editItem) === id;

            if (isEditing) {
              return (
                <InlineForm
                  key={`edit-${id}`}
                  levelCfg={levelCfg} parentId={parentId} item={item}
                  hazardId={hazardId}
                  onSave={() => { setEditItem(null); load(); }}
                  onCancel={() => setEditItem(null)}
                />
              );
            }

            return (
              <div key={id} className="nc-item-row">
                <span className="nc-item-name" title={name}>{name}</span>
                <div className="nc-item-actions">
                  {!levelCfg.isLeaf && (
                    <button
                      className="nc-action-btn"
                      title={t("ncTreePanel.alt", { v: PANEL_LEVELS[PANEL_LEVELS.findIndex(l => l.key === levelCfg.key) + 1]?.plural || t("ncTreePanel.ogeler") })}
                      onClick={() => onDrillDown(item)}
                    >
                      <ChevronRight size={12} />
                    </button>
                  )}
                  <button
                    className="nc-action-btn"
                    title={t("common:duzenle")}
                    onClick={() => { setEditItem(item); setShowAdd(false); }}
                  >
                    <Edit2 size={12} />
                  </button>
                  <button
                    className="nc-action-btn nc-action-btn--danger"
                    title={t("common:sil")}
                    onClick={() => handleDelete(item)}
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Ana Panel Bileşeni ────────────────────────────────────────────────────────
function NcTreePanel({ question, isFormSaved, onClose }) {
  const { t } = useTranslation("formlar");
  // path: [{ levelIdx, id, name }]
  const [path, setPath] = useState([]);

  // Soru değişince path'i sıfırla (render sırasında, effect'siz — React önerilen pattern)
  const [prevQuestionId, setPrevQuestionId] = useState(question?.id);
  if (prevQuestionId !== question?.id) {
    setPrevQuestionId(question?.id);
    setPath([]);
  }

  if (!question) return null;

  const levelIdx   = path.length;
  const levelCfg   = PANEL_LEVELS[levelIdx];
  const parentNode = path.length > 0 ? path[path.length - 1] : null;
  const parentId   = parentNode?.id ?? question.id;

  function drillDown(item) {
    const cfg = PANEL_LEVELS[path.length];
    if (!cfg || cfg.isLeaf) return;
    setPath((prev) => [
      ...prev,
      { levelIdx: path.length, id: getId(cfg, item), name: getName(cfg, item) },
    ]);
  }

  function goBack()  { setPath((prev) => prev.slice(0, -1)); }
  function goTo(i)   { setPath((prev) => prev.slice(0, i + 1)); }

  const notSavedYet = !isFormSaved || question._fromApi !== true;

  return (
    <div className="nc-panel">
      {/* ── Header ─────────────────────────────────────────────────── */}
      <div className="nc-panel-header">
        <div className="d-flex align-items-start justify-content-between gap-2">
          <div className="flex-grow-1 overflow-hidden">
            <div className="nc-panel-label">{t("ncTreePanel.ncAgaci")}</div>
            <div className="nc-panel-question" title={question.ad}>{question.ad}</div>
          </div>
          <button className="btn btn-sm btn-light border flex-shrink-0" onClick={onClose}>
            <X size={13} />
          </button>
        </div>

        {/* Mini breadcrumb */}
        <div className="nc-breadcrumb">
          <button
            className={`nc-bc-btn ${path.length === 0 ? "nc-bc-btn--active-warn" : ""}`}
            onClick={() => setPath([])}
          >
            {t("ncTreePanel.uygunsuzluklar")}
          </button>
          {path.map((seg, i) => (
            <React.Fragment key={`bc-${seg.id}-${i}`}>
              <ChevronRight size={10} className="text-muted flex-shrink-0" />
              <button
                className={`nc-bc-btn ${i === path.length - 1 ? "nc-bc-btn--active-danger" : ""}`}
                onClick={() => goTo(i)}
                title={seg.name}
              >
                {seg.name.length > 16 ? seg.name.slice(0, 16) + "…" : seg.name}
              </button>
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* ── Body ───────────────────────────────────────────────────── */}
      <div className="nc-panel-body">
        {notSavedYet ? (
          <Alert color="warning" fade={false} className="small py-2 mb-0">
            <AlertTriangle size={13} className="me-1" />
            {t("ncTreePanel.ncAgaciEklemekIcinFormu")}
            <strong>{t("ncTreePanel.kaydedin")}</strong>{" "}{t("ncTreePanel.veSayfayiYenileyin")}
          </Alert>
        ) : !levelCfg ? (
          <p className="text-muted small text-center py-3">{t("ncTreePanel.sonSeviyeyeUlasildi")}</p>
        ) : (
          <>
            {/* Geri + üst node bilgisi */}
            {path.length > 0 && (
              <div className="nc-drill-header">
                <button
                  className="btn btn-sm btn-light border d-flex align-items-center gap-1"
                  style={{ fontSize: "0.72rem" }}
                  onClick={goBack}
                >
                  <ChevronLeft size={12} />{" "}{t("common:geri")}
                </button>
                <span className="text-muted small ms-2 text-truncate" title={parentNode.name}>
                  ↳ {parentNode.name}
                </span>
              </div>
            )}

            <LevelList
              key={`${levelCfg.key}-${parentId}`}
              levelCfg={levelCfg}
              parentId={parentId}
              onDrillDown={drillDown}
              hazardId={path.length >= 1 ? path[0].id : null}
            />
          </>
        )}
      </div>
    </div>
  );
}

export default NcTreePanel;

